/**
 * PostPrivateLabelModal Component
 * Modal for creating new private labels
 */
import React, { useState } from 'react';
import { X, Plus, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const PostPrivateLabelModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    genres: '',
    website: '',
    email: '',
    instagram: '',
    soundcloud: '',
    spotify: '',
    accepting_demos: false,
    demo_submission_email: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert('Please enter a label name');
      return;
    }

    if (formData.accepting_demos && !formData.demo_submission_email.trim()) {
      alert('Please enter a demo submission email if accepting demos');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('beatseek_token');
      const genresArray = formData.genres.split(',').map(g => g.trim()).filter(g => g);
      
      const response = await fetch(`${API}/api/private-labels`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description,
          genres: genresArray,
          website: formData.website || null,
          email: formData.email || null,
          accepting_demos: formData.accepting_demos,
          demo_submission_email: formData.accepting_demos ? formData.demo_submission_email : null,
          socials: {
            instagram: formData.instagram || null,
            soundcloud: formData.soundcloud || null,
            spotify: formData.spotify || null
          }
        })
      });

      if (response.ok) {
        onSuccess();
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail || 'Failed to post label'}`);
      }
    } catch (error) {
      console.error('Error posting label:', error);
      alert('Failed to post label');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '0.6rem',
    borderRadius: '8px',
    border: '1px solid rgba(147, 51, 234, 0.3)',
    background: 'rgba(30, 20, 50, 0.8)',
    color: '#fff',
    fontSize: '0.85rem',
    outline: 'none',
    boxSizing: 'border-box'
  };

  const socialInputStyle = {
    width: '100%',
    padding: '0.5rem',
    borderRadius: '6px',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    background: 'rgba(30, 20, 50, 0.6)',
    color: '#fff',
    fontSize: '0.8rem',
    outline: 'none',
    boxSizing: 'border-box'
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.8)',
      backdropFilter: 'blur(10px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10005,
      padding: '1rem'
    }}
    onClick={onClose}
    >
      <div
        data-testid="post-label-modal"
        className="post-label-modal custom-scrollbar"
        style={{
          maxWidth: '500px',
          width: '100%',
          background: 'linear-gradient(135deg, rgba(20, 10, 35, 0.98), rgba(30, 15, 50, 0.98)) padding-box, linear-gradient(135deg, #9333ea, #fbbf24, #9333ea, #fbbf24) border-box',
          borderRadius: '16px',
          border: '3px solid transparent',
          maxHeight: '85vh',
          overflow: 'auto',
          backgroundSize: '200% 200%',
          animation: 'borderFlow 4s linear infinite'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '1rem 1.25rem',
          borderBottom: '1px solid rgba(147, 51, 234, 0.2)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h3 style={{ margin: 0, color: '#fbbf24', fontSize: '1.1rem' }}>
            Post Your Label
          </h3>
          <button
            onClick={onClose}
            data-testid="close-post-label-modal"
            style={{
              background: 'transparent',
              border: 'none',
              color: 'rgba(255,255,255,0.5)',
              cursor: 'pointer',
              padding: '0.25rem'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: '1.25rem' }}>
          {/* Label Name */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.3rem', color: '#fbbf24', fontSize: '0.8rem' }}>
              Label Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Your label name"
              required
              data-testid="label-name-input"
              style={inputStyle}
            />
          </div>

          {/* Genres */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.3rem', color: '#fbbf24', fontSize: '0.8rem' }}>
              Genres (comma-separated)
            </label>
            <input
              type="text"
              value={formData.genres}
              onChange={(e) => setFormData({ ...formData, genres: e.target.value })}
              placeholder="Tech House, Techno, Deep House"
              data-testid="label-genres-input"
              style={inputStyle}
            />
          </div>

          {/* Description */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.3rem', color: '#fbbf24', fontSize: '0.8rem' }}>
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Tell us about your label..."
              rows={3}
              data-testid="label-description-input"
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>

          {/* Website & Email */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.3rem', color: '#fbbf24', fontSize: '0.8rem' }}>
                Website
              </label>
              <input
                type="url"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                placeholder="https://..."
                data-testid="label-website-input"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.3rem', color: '#fbbf24', fontSize: '0.8rem' }}>
                Contact Email
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="contact@label.com"
                data-testid="label-email-input"
                style={inputStyle}
              />
            </div>
          </div>

          {/* Social Links */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', color: '#fbbf24', fontSize: '0.8rem' }}>
              Social Links (optional)
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <input
                type="url"
                value={formData.instagram}
                onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                placeholder="Instagram URL"
                style={socialInputStyle}
              />
              <input
                type="url"
                value={formData.soundcloud}
                onChange={(e) => setFormData({ ...formData, soundcloud: e.target.value })}
                placeholder="SoundCloud URL"
                style={socialInputStyle}
              />
              <input
                type="url"
                value={formData.spotify}
                onChange={(e) => setFormData({ ...formData, spotify: e.target.value })}
                placeholder="Spotify URL"
                style={socialInputStyle}
              />
            </div>
          </div>

          {/* Accepting Demos Section */}
          <div style={{ 
            marginBottom: '1.5rem',
            padding: '1rem',
            borderRadius: '10px',
            background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.1), rgba(147, 51, 234, 0.1))',
            border: '1px solid rgba(34, 197, 94, 0.3)'
          }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.75rem',
              cursor: 'pointer',
              marginBottom: formData.accepting_demos ? '1rem' : '0'
            }}>
              <input
                type="checkbox"
                checked={formData.accepting_demos}
                onChange={(e) => setFormData({ ...formData, accepting_demos: e.target.checked })}
                data-testid="accepting-demos-checkbox"
                style={{
                  width: '20px',
                  height: '20px',
                  accentColor: '#22c55e',
                  cursor: 'pointer'
                }}
              />
              <div>
                <span style={{ color: '#22c55e', fontWeight: '600', fontSize: '0.9rem' }}>
                  📬 Accepting Demos
                </span>
                <p style={{ margin: '0.2rem 0 0 0', color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem' }}>
                  Feature your label in the &quot;Accepting Demos&quot; section
                </p>
              </div>
            </label>
            
            {formData.accepting_demos && (
              <div style={{ marginTop: '0.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.3rem', color: '#22c55e', fontSize: '0.8rem' }}>
                  Demo Submission Email *
                </label>
                <input
                  type="email"
                  value={formData.demo_submission_email}
                  onChange={(e) => setFormData({ ...formData, demo_submission_email: e.target.value })}
                  placeholder="demos@yourlabel.com"
                  required={formData.accepting_demos}
                  data-testid="demo-email-input"
                  style={{
                    ...inputStyle,
                    border: '1px solid rgba(34, 197, 94, 0.4)'
                  }}
                />
                <p style={{ margin: '0.3rem 0 0 0', color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem' }}>
                  Producers will see this email when viewing your label
                </p>
              </div>
            )}
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading || !formData.name.trim()}
            data-testid="submit-label-btn"
            style={{
              width: '100%',
              padding: '0.75rem',
              borderRadius: '8px',
              border: 'none',
              background: loading || !formData.name.trim() 
                ? 'rgba(251, 191, 36, 0.3)' 
                : 'linear-gradient(135deg, #fbbf24, #f59e0b)',
              color: loading || !formData.name.trim() ? 'rgba(0,0,0,0.5)' : '#000',
              fontSize: '0.9rem',
              fontWeight: '600',
              cursor: loading || !formData.name.trim() ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem'
            }}
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />}
            {loading ? 'Posting...' : 'Post Label'}
          </button>
        </form>
      </div>

      {/* Styles for gradient animation and scrollbar */}
      <style>{`
        @keyframes borderFlow {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .post-label-modal::-webkit-scrollbar {
          width: 5px;
        }
        .post-label-modal::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.3);
          border-radius: 10px;
          margin: 12px 0;
        }
        .post-label-modal::-webkit-scrollbar-thumb {
          background: linear-gradient(135deg, #9333ea, #fbbf24);
          border-radius: 10px;
        }
      `}</style>
    </div>
  );
};

export default PostPrivateLabelModal;
